<template>
  <IntercomModal v-model:show="isOpen" :title="$t('common.takePhoto', 'Take Photo')" width="480px">
    <div class="webcam-capture">
      <!-- Camera Preview -->
      <div class="camera-container" v-if="!capturedImage">
        <video
          ref="videoRef"
          autoplay
          playsinline
          class="camera-preview"
          :class="{ loading: isLoading }"
        ></video>
        <div v-if="isLoading" class="camera-loading">
          <span class="loading-spinner"></span>
          <p>{{ $t('common.startingCamera', 'Starting camera...') }}</p>
        </div>
        <div v-if="cameraError" class="camera-error">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          <p>{{ cameraError }}</p>
          <button class="btn-retry" @click="startCamera">
            {{ $t('common.retry', 'Retry') }}
          </button>
        </div>
      </div>

      <!-- Captured Preview -->
      <div class="preview-container" v-else>
        <img :src="capturedImage" alt="Captured photo" class="captured-preview" />
      </div>

      <!-- Hidden canvas for capture -->
      <canvas ref="canvasRef" style="display: none;"></canvas>

      <!-- Actions -->
      <div class="capture-actions">
        <template v-if="!capturedImage">
          <button
            class="btn-capture"
            @click="capturePhoto"
            :disabled="isLoading || !!cameraError"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="10"/>
            </svg>
          </button>
        </template>
        <template v-else>
          <button class="btn-secondary" @click="retake">
            {{ $t('common.retake', 'Retake') }}
          </button>
          <button class="btn-primary" @click="confirmCapture" :disabled="isUploading">
            {{ isUploading ? $t('common.uploading', 'Uploading...') : $t('common.usePhoto', 'Use Photo') }}
          </button>
        </template>
      </div>
    </div>
  </IntercomModal>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { IntercomModal } from '@shared/components/intercom'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:show', 'captured'])

const { t } = useI18n()

const isOpen = ref(props.show)
const videoRef = ref(null)
const canvasRef = ref(null)
const stream = ref(null)
const capturedImage = ref(null)
const capturedBlob = ref(null)
const isLoading = ref(false)
const isUploading = ref(false)
const cameraError = ref(null)

// Sync with parent
watch(() => props.show, (val) => {
  isOpen.value = val
  if (val) {
    startCamera()
  } else {
    stopCamera()
  }
})

watch(isOpen, (val) => {
  emit('update:show', val)
  if (!val) {
    stopCamera()
    capturedImage.value = null
    capturedBlob.value = null
  }
})

async function startCamera() {
  isLoading.value = true
  cameraError.value = null

  try {
    stream.value = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 }
      },
      audio: false
    })

    if (videoRef.value) {
      videoRef.value.srcObject = stream.value
    }
  } catch (err) {
    console.error('Camera access error:', err)
    if (err.name === 'NotAllowedError') {
      cameraError.value = t('common.cameraPermissionDenied', 'Camera permission denied. Please allow camera access.')
    } else if (err.name === 'NotFoundError') {
      cameraError.value = t('common.cameraNotFound', 'No camera found on this device.')
    } else {
      cameraError.value = t('common.cameraError', 'Failed to access camera.')
    }
  } finally {
    isLoading.value = false
  }
}

function stopCamera() {
  if (stream.value) {
    stream.value.getTracks().forEach(track => track.stop())
    stream.value = null
  }
}

function capturePhoto() {
  if (!videoRef.value || !canvasRef.value) return

  const video = videoRef.value
  const canvas = canvasRef.value
  const ctx = canvas.getContext('2d')

  // Set canvas size to video size
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight

  // Draw current video frame
  ctx.drawImage(video, 0, 0)

  // Convert to blob
  canvas.toBlob((blob) => {
    capturedBlob.value = blob
    capturedImage.value = URL.createObjectURL(blob)
  }, 'image/jpeg', 0.85)
}

function retake() {
  if (capturedImage.value) {
    URL.revokeObjectURL(capturedImage.value)
  }
  capturedImage.value = null
  capturedBlob.value = null
}

function confirmCapture() {
  if (capturedBlob.value) {
    emit('captured', capturedBlob.value)
    isOpen.value = false
  }
}

onUnmounted(() => {
  stopCamera()
  if (capturedImage.value) {
    URL.revokeObjectURL(capturedImage.value)
  }
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.webcam-capture {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.camera-container,
.preview-container {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  background: var(--color-gray-900);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.camera-preview,
.captured-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.camera-preview.loading {
  opacity: 0;
}

.camera-loading,
.camera-error {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  color: var(--color-gray-300);
  text-align: center;
  padding: var(--space-4);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-gray-600);
  border-top-color: var(--color-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.camera-error svg {
  color: var(--color-red-400);
}

.btn-retry {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  color: white;
  background: var(--color-blue);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
}

.capture-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  padding: var(--space-2) 0;
}

.btn-capture {
  width: 64px;
  height: 64px;
  padding: 0;
  background: transparent;
  border: 4px solid var(--color-gray-300);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-capture:hover:not(:disabled) {
  border-color: var(--color-blue);
}

.btn-capture:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-capture svg {
  color: var(--color-red-500);
}

.btn-secondary {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: var(--color-gray-100);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.15s;
}

.btn-secondary:hover {
  background: var(--color-gray-200);
}

.btn-primary {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: white;
  background: var(--color-blue);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-blue-dark, #0046cc);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
